import { createSlice } from "@reduxjs/toolkit";
import axios from "axios";

export const prizeDistributionRulesSlice = createSlice({
    name: "prizeDistributionRules",
    initialState: {
        page: 0,
        pageCount: 0,

        success: false,
        fail: false,

        listData: null,
        detailData: null,

        validationData: null,
        prizesListData: null,
        
        responseData: null,
        errorDetailData: null,

        deleteSuccess: false,
        deleteFail: false,
    },
    reducers: {
        doGetListSuccess: (state, action) => {
            state.listData = action.payload.data;
            state.page = action.payload?.pagination?.page;
            state.pageCount = action.payload?.pagination?.totalPages;
        },
        doGetListFail: (state, action) => {
            state.listData = null;
            state.page = 0;
            state.pageCount = 0;
        },
        doGetDetailSuccess: (state, action) => {
            state.detailData = action.payload.data;
        },
        doGetDetailFail: (state, action) => {
            state.detailData = null;
        },
        doAddSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
            state.responseData = action.payload;
        },
        doAddFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        doUpdateSuccess: (state, action) => {
            state.success = true;
            state.fail = false;
        },
        doUpdateFail: (state, action) => {
            state.success = false;
            state.fail = true;
            state.errorDetailData = action.payload;
        },
        doDeleteSuccess: (state, action) => {
            state.deleteSuccess = true;
            state.deleteFail = false;
        },
        doDeleteFail: (state, action) => {
            state.deleteSuccess = false;
            state.deleteFail = true;
            state.errorDetailData = action.payload;
        },
        doValidateCoverageSuccess: (state, action) => {
            state.validationData = action.payload.data;
        },
        doValidateCoverageFail: (state, action) => {
            state.validationData = null;
        },
        doGetPrizesListSuccess: (state, action) => {
            state.prizesListData = action.payload.data;
        },
        doGetPrizesListFail: (state, action) => {
            state.prizesListData = null;
        },
        resetStateSuccess: (state, action) => {
            state.success = false;
            state.fail = false;
            state.detailData = null;
            state.validationData = null;
            state.errorDetailData = null;
            state.deleteSuccess = false;
            state.deleteFail = false;
        },
    },
});

/**
 * Get prize distribution rules list with pagination and filters
 */
export const doGetList = (param) => async (dispatch) => {
    try {
        let url = `/api/admin/prize-distribution-rules?page=${param.page}&size=${param.limit}`;

        if (param.filter) {
            url += `&filter=${encodeURIComponent(param.filter)}`;
        }

        const response = await axios.get(url);
        dispatch(doGetListSuccess(response.data));
    } catch (error) {
        dispatch(doGetListFail(error.response));
    }
};

/**
 * Get single rule detail
 */
export const doGetDetail = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/prize-distribution-rules/${param.rule_id}`;
        const response = await axios.get(url);
        dispatch(doGetDetailSuccess(response.data));
    } catch (error) {
        dispatch(doGetDetailFail(error.response));
    }
};

/**
 * Add new prize distribution rule
 */
export const doAdd = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/prize-distribution-rules`;
        const response = await axios.post(url, param);
        dispatch(doAddSuccess(response.data));
    } catch (error) {
        dispatch(doAddFail(error.response));
    }
};

/**
 * Update existing rule
 */
export const doUpdate = (param, id) => async (dispatch) => {
    try {
        const url = `/api/admin/prize-distribution-rules/${id}`;
        const response = await axios.put(url, param);
        dispatch(doUpdateSuccess(response.data));
    } catch (error) {
        dispatch(doUpdateFail(error.response));
    }
};

/**
 * Delete rule (soft delete)
 */
export const doDelete = (param) => async (dispatch) => {
    try {
        const url = `/api/admin/prize-distribution-rules/${param.rule_id}`;
        const response = await axios.delete(url);
        dispatch(doDeleteSuccess(response.data));
    } catch (error) {
        dispatch(doDeleteFail(error.response));
    }
};

/**
 * Validate prize distribution coverage
 */
export const doValidateCoverage = () => async (dispatch) => {
    try {
        const url = `/api/admin/prize-distribution-rules/validate-coverage`;
        const response = await axios.get(url);
        dispatch(doValidateCoverageSuccess(response.data));
    } catch (error) {
        dispatch(doValidateCoverageFail(error.response));
    }
};

/**
 * Get treasure box prizes for dropdown (from existing API)
 */
export const doGetPrizesList = () => async (dispatch) => {
    try {
        const url = `/api/admin/treasure-box?page=1&size=1000`;
        const response = await axios.get(url);
        dispatch(doGetPrizesListSuccess(response.data));
    } catch (error) {
        dispatch(doGetPrizesListFail(error.response));
    }
};

/**
 * Reset state
 */
export const resetState = () => async (dispatch) => {
    dispatch(resetStateSuccess(false));
};

// Export actions
export const {
    doGetListSuccess,
    doGetListFail,

    doGetDetailSuccess,
    doGetDetailFail,

    doAddSuccess,
    doAddFail,

    doUpdateSuccess,
    doUpdateFail,

    doDeleteSuccess,
    doDeleteFail,

    doValidateCoverageSuccess,
    doValidateCoverageFail,

    doGetPrizesListSuccess,
    doGetPrizesListFail,

    resetStateSuccess,
} = prizeDistributionRulesSlice.actions;

// Selectors
export const getListData = (state) => state.prizeDistributionRules?.listData;
export const getCurrentPage = (state) => state.prizeDistributionRules?.page;
export const getPageCount = (state) => state.prizeDistributionRules?.pageCount;

export const getDetail = (state) => state.prizeDistributionRules?.detailData;
export const getSuccess = (state) => state.prizeDistributionRules?.success;
export const getFail = (state) => state.prizeDistributionRules?.fail;

export const getValidationData = (state) => state.prizeDistributionRules?.validationData;

export const getPrizesListData = (state) => state.prizeDistributionRules?.prizesListData;
export const getErrorData = (state) => state.prizeDistributionRules?.errorDetailData;

export const getDeleteSuccess = (state) => state.prizeDistributionRules?.deleteSuccess;
export const getDeleteFail = (state) => state.prizeDistributionRules?.deleteFail;

export default prizeDistributionRulesSlice.reducer;
